import { useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, ChevronRight } from "lucide-react";
import { RecentChanges } from "../components/home/RecentChanges";
import { PlansAndFeatures } from "../components/home/PlansAndFeatures";
import { TrendingPlayers } from "../components/home/TrendingPlayers";
import { NewsFeed } from "../components/home/NewsFeed";
import { FeedbackModal } from "../components/modals/FeedbackModal";

export const HomePage = () => {
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  return (
    <div className="min-h-screen bg-slate-50">
      <button
        onClick={() => setIsFeedbackOpen(true)}
        className="fixed bottom-5 right-5 z-50 inline-flex items-center gap-2 px-3.5 py-2.5 rounded-full shadow-lg bg-blue-600 text-white hover:bg-blue-700 focus:outline-none"
        aria-label="Send feedback"
      >
        <MessageCircle className="w-4 h-4" />
        <span className="hidden sm:inline text-sm">Feedback</span>
      </button>

      <div className="container max-w-6xl mx-auto px-5 sm:px-8 lg:px-10 py-6 sm:py-8">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 sm:p-6 mb-6">
          <div className="grid lg:grid-cols-12 gap-5 sm:gap-6 items-start">
            <div className="lg:col-span-8">
              <h1 className="mt-1.5 text-3xl sm:text-4xl font-bold leading-tight tracking-tight">
                <span className="text-gray-900">D3 Dashboard</span>
              </h1>
              <p className="mt-2.5 text-sm text-slate-600 max-w-2xl">
                College baseball data infrastructure for player evaluation, game analysis, and scouting workflows.
              </p>
              <div className="flex flex-wrap gap-2.5 pt-4">
                <Link
                  to="/data"
                  className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
                >
                  Open Data
                </Link>
                <Link
                  to="/charting"
                  className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-white text-blue-700 text-sm font-medium border border-blue-200 hover:bg-blue-50 transition-colors"
                >
                  Open Charting
                </Link>
              </div>
            </div>
            <div className="lg:col-span-4">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <div className="grid grid-cols-3 gap-3">
                  <Metric title="STARTED" value="2025" />
                  <Metric title="COVERAGE" value="D1-D3" />
                  <Metric title="DATA" value="6 yrs" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-2">
          <p className="text-[11px] font-semibold tracking-[0.16em] text-slate-500 uppercase">Features</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
          <FeatureCard
            title="Charting"
            description="Chart pitches and hits during games or bullpen sessions. Upload data from Rapsodo, or Trackman."
            linkTo="/charting"
          />
          <FeatureCard
            title="Stats & Data"
            description="Browse player stats, percentiles, spray charts, and team data across D1-D3 from 2021+."
            linkTo="/data"
          />
          <FeatureCard
            title="Scouting Reports"
            description="Build team-based reports with player stats, notes, and writeups. Export to PDF."
            linkTo="/scouting"
          />
          <FeatureCard
            title="Leaderboards"
            description="Value, situational, baserunning, splits, batted ball, and hot/cold/trending views."
            linkTo="/leaderboards"
          />
          <FeatureCard
            title="Game Pages"
            description="Win expectancy, leverage, and run expectancy charts with game-level data."
            linkTo="/scoreboard"
          />
          <FeatureCard
            title="Player Lists"
            description="Create and manage custom lists of players you&apos;re tracking."
            linkTo="/player-lists"
          />
        </div>

        <div className="mb-2">
          <p className="text-[11px] font-semibold tracking-[0.16em] text-slate-500 uppercase">Live Widgets</p>
        </div>
        <div className="grid lg:grid-cols-3 gap-4 mb-6">
          <div className="lg:col-span-1">
            <div className="h-[300px]">
              <TrendingPlayers compact limit={6} />
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="h-[300px]">
              <RecentChanges />
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="h-[300px]">
              <PlansAndFeatures />
            </div>
          </div>
        </div>

        <div className="mb-2">
          <p className="text-[11px] font-semibold tracking-[0.16em] text-slate-500 uppercase">External Feed</p>
        </div>
        <div className="mb-6">
          <NewsFeed />
        </div>

        <div className="w-full flex justify-center mb-5">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-5 w-full max-w-6xl text-center">
            <p className="text-sm text-slate-600 leading-relaxed">
              Questions, bug reports, or feedback? Contact me at{" "}
              <a href="mailto:jackkelly12902@gmail.com" className="text-blue-600 hover:text-blue-800 font-medium underline-offset-2 hover:underline">
                jackkelly12902@gmail.com
              </a>{" "}
              or{" "}
              <a href="https://twitter.com/jkelly_1214" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 font-medium underline-offset-2 hover:underline">
                @jkelly_1214
              </a>
            </p>
          </div>
        </div>
      </div>
      <FeedbackModal isOpen={isFeedbackOpen} onClose={() => setIsFeedbackOpen(false)} />
    </div>
  );
};

const FeatureCard = ({ title, description, linkTo }) => {
  return (
    <Link
      to={linkTo}
      className="group bg-white rounded-lg p-4 border border-slate-200 hover:border-slate-300 transition-colors"
    >
      <h3 className="text-sm font-semibold text-slate-900 mb-1.5">{title}</h3>
      <p className="text-xs text-slate-600 leading-relaxed mb-3">{description}</p>
      <div className="inline-flex items-center text-xs font-medium text-blue-700 group-hover:text-blue-800 transition-colors">
        Open Module
        <ChevronRight className="ml-1 w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
      </div>
    </Link>
  );
};

const Metric = ({ title, value }) => (
  <div>
    <p className="text-[10px] tracking-[0.12em] text-slate-500">{title}</p>
    <p className="mt-0.5 text-lg font-semibold text-slate-900">{value}</p>
  </div>
);